import type { IPastedText } from "@frontend-shared/types";
import { createPastedText, setScopedEntry } from "@frontend-shared/utils";
import { useCallback, useEffect, useRef, useState } from "react";

// NOTE: NSAI-6791 — the long pastes held beside a chat's draft, kept the way the
// draft itself is (see useChatDraft): in memory, per chat scope, capped by
// setScopedEntry, never written to storage, and dropped on sign-out.
const chatPastedTextStore = new Map<string, IPastedText[]>();

export const resetChatPastedTextStore = (): void => {
  chatPastedTextStore.clear();
};

interface IMigratePastedTextsProps {
  fromScopeKey: string;
  toScopeKey: string;
}

interface IUpdatePastedTextProps {
  id: string;
  content: string;
}

interface IRemoveSentPastedTextsProps {
  sent: readonly IPastedText[];
  scopeKey: string;
}

interface IUseChatPastedTextsReturn {
  pastedTexts: IPastedText[];
  addPastedText: (content: string) => IPastedText;
  restorePastedTexts: (contents: string[]) => void;
  removeSentPastedTexts: (props: IRemoveSentPastedTextsProps) => void;
  updatePastedText: (props: IUpdatePastedTextProps) => void;
  removePastedText: (id: string) => void;
  clearPastedTexts: (scopeKey?: string) => void;
  migratePastedTexts: (props: IMigratePastedTextsProps) => void;
}

const EMPTY_PASTED_TEXTS: IPastedText[] = [];

export const useChatPastedTexts = (
  scopeKey: string,
): IUseChatPastedTextsReturn => {
  // NOTE: The active key lives in a ref so every callback below stays stable,
  // exactly as in useChatDraft.
  const scopeKeyRef = useRef<string>(scopeKey);
  const [pastedTexts, setPastedTexts] = useState<IPastedText[]>(
    () => chatPastedTextStore.get(scopeKey) ?? EMPTY_PASTED_TEXTS,
  );

  useEffect(() => {
    scopeKeyRef.current = scopeKey;
    setPastedTexts(chatPastedTextStore.get(scopeKey) ?? EMPTY_PASTED_TEXTS);
  }, [scopeKey]);

  const commit = useCallback((next: IPastedText[]): void => {
    const key = scopeKeyRef.current;
    if (next.length > 0) {
      setScopedEntry({ store: chatPastedTextStore, key, value: next });
    } else {
      chatPastedTextStore.delete(key);
    }
    setPastedTexts(next);
  }, []);

  // NOTE: Reads the store rather than state, so two pastes handled before a
  // re-render both land instead of the second overwriting the first.
  const readActive = useCallback(
    (): IPastedText[] =>
      chatPastedTextStore.get(scopeKeyRef.current) ?? EMPTY_PASTED_TEXTS,
    [],
  );

  const addPastedText = useCallback(
    (content: string): IPastedText => {
      const pastedText = createPastedText(content);
      commit([...readActive(), pastedText]);
      return pastedText;
    },
    [commit, readActive],
  );

  // NOTE: Puts the pastes of a failed send back as cards. One whose exact text
  // is already a card is skipped, so a restore can never double a paste.
  const restorePastedTexts = useCallback(
    (contents: string[]): void => {
      const current = readActive();
      const existing = new Set(current.map((pastedText) => pastedText.content));
      const restored = contents
        .filter((content) => !existing.has(content))
        .map((content) => createPastedText(content));
      if (restored.length === 0) return;
      commit([...current, ...restored]);
    },
    [commit, readActive],
  );

  // NOTE: After a send, takes out only the cards that rode it — same id, same
  // text — from the scope it was sent from. A card added or edited while the
  // send's upload was still running stays for the next message.
  const removeSentPastedTexts = useCallback(
    ({ sent, scopeKey: sentScopeKey }: IRemoveSentPastedTextsProps): void => {
      if (sent.length === 0) return;
      const sentContentById = new Map(
        sent.map((pastedText) => [pastedText.id, pastedText.content]),
      );
      const current =
        chatPastedTextStore.get(sentScopeKey) ?? EMPTY_PASTED_TEXTS;
      const next = current.filter(
        (pastedText) =>
          sentContentById.get(pastedText.id) !== pastedText.content,
      );
      if (next.length === current.length) return;
      if (next.length > 0) {
        setScopedEntry({
          store: chatPastedTextStore,
          key: sentScopeKey,
          value: next,
        });
      } else {
        chatPastedTextStore.delete(sentScopeKey);
      }
      if (sentScopeKey === scopeKeyRef.current) {
        setPastedTexts(next);
      }
    },
    [],
  );

  const updatePastedText = useCallback(
    ({ id, content }: IUpdatePastedTextProps): void => {
      const current = readActive();
      if (!current.some((pastedText) => pastedText.id === id)) return;
      commit(
        current.map((pastedText) =>
          pastedText.id === id ? { ...pastedText, content } : pastedText,
        ),
      );
    },
    [commit, readActive],
  );

  const removePastedText = useCallback(
    (id: string): void => {
      commit(readActive().filter((pastedText) => pastedText.id !== id));
    },
    [commit, readActive],
  );

  // NOTE: Takes the scope captured at send time, like clearDraft, so a chat
  // switch during an awaited upload cannot clear the wrong chat's pastes.
  const clearPastedTexts = useCallback((scopeKeyToClear?: string): void => {
    const key = scopeKeyToClear ?? scopeKeyRef.current;
    chatPastedTextStore.delete(key);
    if (key === scopeKeyRef.current) {
      setPastedTexts(EMPTY_PASTED_TEXTS);
    }
  }, []);

  // NOTE: A new chat gaining its id moves the draft to the new scope; the
  // pastes that belong to that draft move with it (see migrateDraft).
  const migratePastedTexts = useCallback(
    ({ fromScopeKey, toScopeKey }: IMigratePastedTextsProps): void => {
      if (fromScopeKey === toScopeKey) return;
      const carried = chatPastedTextStore.get(fromScopeKey);
      if (!carried) return;
      chatPastedTextStore.delete(fromScopeKey);
      if (!chatPastedTextStore.has(toScopeKey)) {
        setScopedEntry({
          store: chatPastedTextStore,
          key: toScopeKey,
          value: carried,
        });
      }
      if (toScopeKey === scopeKeyRef.current) {
        setPastedTexts(
          chatPastedTextStore.get(toScopeKey) ?? EMPTY_PASTED_TEXTS,
        );
      }
    },
    [],
  );

  return {
    pastedTexts,
    addPastedText,
    restorePastedTexts,
    removeSentPastedTexts,
    updatePastedText,
    removePastedText,
    clearPastedTexts,
    migratePastedTexts,
  };
};
